import { useContext } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { PollingStatus } from '@/types/order';
import { useApplicationState } from '@/hooks/useApplicationState';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useIsLandscape } from '@/hooks/useIsLandscape';
import { StripePaymentHandlerPage } from '@/pages/StripePaymentHandlerPage';
import { shouldRenderPaymentHandlerPage } from '@/utils/location';
import { Modal } from '@/components/Common/Modal';
import { Details } from '@/components/Details/Details';
import { Payment } from '@/components/Payment/Payment';
import { ProcessingLoader } from '@/components/ProcessingLoader/ProcessingLoader';
import { Card3DSForm } from '@/components/Card3DSForm/Card3DSForm';
import { ScreenDrawer } from '@/components/ScreenDrawer/ScreenDrawer';
import { RedirectPayment } from '@/components/RedirectPayment/RedirectPayment';
import { SuccessScreen } from '@/components/SuccessScreen/SuccessScreen';
import { XButton } from '@/components/XButton/XButton';
import { hasPublisherHaveBackgroundImage } from '@/utils/publisher';
import { cn } from '@/utils/cn';
import type { TemplateViewProps } from '@/templates/CheckoutTemplate';
import { PromoCode } from '@/components/PromoCode/PromoCode';

/**
 * Compact single-column view: order details on top, the one configured
 * payment method directly below. Used by SINGLE_PAYMENT_METHOD.
 */
export const OnePaymentView = ({ statusOrderPolling }: TemplateViewProps) => {
  const context = useContext(CheckoutContext);
  const {
    redirectPayment: { redirectUrl, showRedirect },
    successScreen,
    boot,
    template,
    actions: { closeCheckout },
  } = context;
  useApplicationState();
  const isMobile = useIsMobile();
  const landscape = useIsLandscape();

  if (shouldRenderPaymentHandlerPage()) {
    return <StripePaymentHandlerPage />;
  }

  // The landscape phone band is a touch device, not desktop — render the same
  // single natural-scroll column the mobile layout uses (Details on top, the
  // one method below, centered) instead of the desktop centered-card +
  // `justify-between` split that left the payment stranded below the fold.
  const stackAsColumn = isMobile || landscape;

  // Publisher background image rides behind XButton + Details (full-width),
  // ending at the divider that sits at the end of Details' compact layout —
  // so the top of the screen carries the publisher art the same way
  // DEFAULT/PREFERRED do, instead of being squeezed into the
  // max-w-[400px] inner card. Landscape phones prefer the mobile asset too.
  const hasCheckoutBackground = hasPublisherHaveBackgroundImage(boot?.checkoutTheme, template.type);
  const checkoutBackgroundImage = hasCheckoutBackground
    ? stackAsColumn
      ? boot?.checkoutTheme?.mobileBgImage || boot?.checkoutTheme?.backgroundImage
      : boot?.checkoutTheme?.backgroundImage
    : undefined;

  return (
    <Modal>
      {/*
       * Mobile scrolls as ONE natural column (like PREFERRED/FTD): the bg band
       * + Details and the Payment column stack and the whole modal scrolls when
       * the content (open details + card form + footer) is taller than the
       * viewport. Putting `overflow-y-auto` on the OUTER column — and letting
       * the Payment column size to its content instead of an inner
       * `flex-1 overflow-y-auto` box — is what stops the form/footer from
       * overlapping in a cramped fixed-height region.
       *
       * Desktop keeps the centered-card layout (`justify-between` + an
       * inner-scrolling Payment column) unchanged.
       */}
      <div
        data-testid="checkout-container"
        className={cn(
          'flex flex-col items-center bg-white w-full h-full pb-4',
          stackAsColumn ? 'overflow-y-auto' : 'justify-between'
        )}
      >
        <div
          data-testid="one-payment-background"
          className="flex flex-col items-center w-full shrink-0 bg-cover bg-top"
          style={checkoutBackgroundImage ? { backgroundImage: `url(${checkoutBackgroundImage})` } : undefined}
        >
          <XButton onClick={() => closeCheckout(context)} />
          <div className="flex flex-col w-full max-w-[450px] md:max-w-[400px] px-4 mt-3 md:mt-10">
            {/* Single column: details must be content-height so the hero band
                stays short and the payment sits right below (not full-100dvh,
                which is only for the two-column hero). */}
            <Details landscapeFillHeight={false} />
          </div>
        </div>
        {isMobile && <PromoCode />}
        <div
          className={cn(
            'flex flex-col w-full max-w-[450px] md:max-w-[400px] px-4 gap-0',
            // `flex-1` lets the payment column grow to fill the space left
            // below the (content-height) details band. Combined with the
            // Payment section's `h-full justify-between`, this pins the
            // Footer to the bottom of the viewport when the content is
            // short, while still letting the whole column scroll naturally
            // (via the outer `overflow-y-auto`) when it's tall.
            stackAsColumn ? 'flex-1' : 'flex-1 overflow-y-auto'
          )}
        >
          <Payment />
        </div>
        <Card3DSForm />
        <ProcessingLoader />
        {stackAsColumn && <ScreenDrawer />}
        {(redirectUrl || showRedirect) && <RedirectPayment />}
        {successScreen?.isOpen && statusOrderPolling === PollingStatus.SUCCESS && <SuccessScreen />}
      </div>
    </Modal>
  );
};
